<script setup lang="ts">
// The optional AI step that follows any completed tree walk. No signup, no
// email: the backend mints an anonymous device cookie on the first call and
// meters the free allowance against the device + IP. Plain elements only —
// this renders on sites without a component library, themed via tokens.
import { computed, ref } from 'vue'

import { ApiError, postJson } from '../api/client'
import type { Answer, DecisionTree } from '../diagnostic/types'
import type { AiDiagnosis, DiagnosisResponse } from '../api/types'

const props = defineProps<{
    tree: DecisionTree
    answers: Answer[]
    /** Short pitch above the button; defaults to a generic one. */
    pitch?: string
}>()

const emit = defineEmits<{ result: [DiagnosisResponse] }>()

const loading = ref(false)
const response = ref<DiagnosisResponse | null>(null)
const error = ref<{ title: string; body: string } | null>(null)
// One idempotency key per run; regenerated only for an explicit re-run.
let idempotencyKey = crypto.randomUUID()

const result = computed<AiDiagnosis | null>(() => response.value?.result ?? null)

const actionLabel = computed(() => {
    switch (result.value?.recommended_action) {
        case 'repair':
            return 'Lean repair'
        case 'replace':
            return 'Lean replace'
        case 'either':
            return 'Either works'
        default:
            return 'Get a pro opinion'
    }
})

async function run() {
    if (loading.value) return
    loading.value = true
    error.value = null
    try {
        const res = await postJson<DiagnosisResponse>('/api/diagnosis', {
            idempotency_key: idempotencyKey,
            tree_id: props.tree.id,
            tree_version: props.tree.version,
            answers: props.answers,
        })
        response.value = res
        emit('result', res)
    } catch (err) {
        if (err instanceof ApiError && err.code === 'ai_cap_reached') {
            error.value = {
                title: 'Free AI limit reached',
                body: 'This device has used all of its free AI analyses. Running the AI costs us real money, so each device gets a fixed allowance.',
            }
        } else if (err instanceof ApiError && err.code === 'ai_unavailable') {
            error.value = {
                title: 'AI temporarily unavailable',
                body: 'The analysis service is paused right now — please try again a little later.',
            }
        } else if (err instanceof ApiError && err.code === 'rate_limited') {
            error.value = {
                title: 'Slow down a moment',
                body: 'Too many analysis requests from your connection — give it a few minutes.',
            }
        } else {
            // A failed call is never charged; a fresh key makes retry safe.
            idempotencyKey = crypto.randomUUID()
            error.value = {
                title: 'Analysis failed',
                body: 'Something went wrong — you were not charged. Please try again.',
            }
        }
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div class="ai-panel">
        <!-- Offer -->
        <template v-if="!result">
            <div class="offer">
                <div class="offer-text">
                    <h4>Want the AI's read on this?</h4>
                    <p class="muted">
                        {{
                            pitch ??
                            'One free AI analysis of your exact answers — likely causes, honest cost ranges, and what to do next. No signup, no email.'
                        }}
                    </p>
                </div>
                <button class="cta" type="button" :disabled="loading" @click="run">
                    {{ loading ? 'Analyzing…' : error ? 'Try again' : 'Run the free AI analysis' }}
                </button>
            </div>
            <div v-if="error" class="error">
                <strong>{{ error.title }}</strong>
                <p class="muted">{{ error.body }}</p>
            </div>
        </template>

        <!-- Result -->
        <template v-else>
            <div class="head">
                <span class="badge">AI analysis</span>
                <span class="action" :data-action="result.recommended_action">{{ actionLabel }}</span>
            </div>
            <h4>{{ result.diagnosis }}</h4>
            <p class="summary">{{ result.summary }}</p>

            <div class="grid">
                <div class="cell">
                    <span class="label">Likely causes</span>
                    <ul>
                        <li v-for="c in result.likely_causes" :key="c.cause">
                            {{ c.cause }} <em class="muted">({{ c.likelihood }})</em>
                        </li>
                    </ul>
                </div>
                <div class="cell">
                    <span class="label">Ballpark costs</span>
                    <p>
                        Repair ${{ result.estimated_repair_cost.low_usd.toLocaleString() }}–{{
                            result.estimated_repair_cost.high_usd.toLocaleString()
                        }}<br />
                        Replace ${{ result.estimated_replacement_cost.low_usd.toLocaleString() }}–{{
                            result.estimated_replacement_cost.high_usd.toLocaleString()
                        }}
                    </p>
                    <span class="label">Remaining life</span>
                    <p>{{ result.remaining_life_years.low }}–{{ result.remaining_life_years.high }} years</p>
                </div>
            </div>

            <div v-if="result.next_steps.length" class="cell">
                <span class="label">Next steps</span>
                <ol>
                    <li v-for="s in result.next_steps" :key="s">{{ s }}</li>
                </ol>
            </div>

            <div v-if="result.red_flags.length" class="flags">
                <strong>⚠ Watch for:</strong>
                <ul>
                    <li v-for="f in result.red_flags" :key="f">{{ f }}</li>
                </ul>
            </div>

            <p class="muted fine">
                AI-generated guidance, confidence {{ result.confidence.replace('_', ' ') }} — a
                licensed technician should confirm before you spend money.
                {{ response!.ai_limit - response!.ai_used }} free analyses left on this device.
            </p>
        </template>
    </div>
</template>

<style scoped lang="scss">
.ai-panel {
    margin-top: var(--site-space-md);
    padding: var(--site-space-lg);
    border: 1px solid var(--site-accent);
    border-radius: var(--site-radius-md);
    background: var(--site-accent-soft);

    h4 {
        margin: 0 0 var(--site-space-xs);
    }
}

.offer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--site-space-lg);
    flex-wrap: wrap;

    .offer-text {
        flex: 1 1 18rem;

        p {
            margin: 0;
            font-size: 0.95rem;
        }
    }

    button {
        flex-shrink: 0;
    }
}

.error {
    margin-top: var(--site-space-md);
    padding: var(--site-space-md);
    border: 1px solid var(--site-danger);
    border-radius: var(--site-radius-md);
    background: var(--site-surface);

    p {
        margin: var(--site-space-xs) 0 0;
        font-size: 0.9rem;
    }
}

.head {
    display: flex;
    align-items: center;
    gap: var(--site-space-sm);
    margin-bottom: var(--site-space-sm);
}

.badge {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--site-accent-contrast, var(--site-text));
    background: var(--site-accent);
    padding: 0.2rem 0.55rem;
    border-radius: var(--site-radius-pill, 999px);
}

.action {
    font-weight: 700;
    font-size: 0.9rem;

    &[data-action='replace'] {
        color: var(--site-danger);
    }

    &[data-action='repair'] {
        color: var(--site-success);
    }
}

.summary {
    margin: 0 0 var(--site-space-md);
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--site-space-md);
    margin-bottom: var(--site-space-md);
}

.cell {
    .label {
        display: block;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--site-text-muted);
        margin-bottom: 0.3rem;
    }

    ul,
    ol {
        margin: 0 0 var(--site-space-sm);
        padding-left: 1.2rem;

        li {
            margin-bottom: 0.25rem;
        }
    }

    p {
        margin: 0 0 var(--site-space-sm);
    }
}

.flags {
    border: 1px solid var(--site-warning);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-sm) var(--site-space-md);
    margin-bottom: var(--site-space-md);
    background: var(--site-surface);

    ul {
        margin: 0.3rem 0 0;
        padding-left: 1.2rem;
    }
}

.fine {
    margin: 0;
    font-size: 0.82rem;
}
</style>
